/**
 * <wr-timeline> custom element
 *
 * Desktop/tablet (>=768px): GSAP slot slideshow — active slide is largest.
 *
 * Mobile (<768px): large active year above a divider; row shows the next
 * slides (active hidden). slidesPerView 2.25 with draggable swipe navigation.
 */

class WrTimeline extends HTMLElement {
  constructor() {
    super();
    this.slides = [];
    this.activeIndex = 0;
    this.isAnimating = false;
    this.isInitialized = false;
    this.gap = 24;
    this.mobileGap = 16;
    this.mobileSlidesPerView = 2.25;
    this.totalSlides = 0;
    this.loop = false;
    this.desktopVisible = 6;
    this.handleResize = this.handleResize.bind(this);
    this.handleSlideClick = this.handleSlideClick.bind(this);
    this.handleSlideKey = this.handleSlideKey.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handlePointerCancel = this.handlePointerCancel.bind(this);
    this.pointerStartX = 0;
    this.pointerStartY = 0;
    this.pointerId = null;
    this.isDragging = false;
    this.dragOffset = 0;
    this.touchMoved = false;
  }

  connectedCallback() {
    if (this.isInitialized) return;
    this.waitForGsap().then(() => this.init());
  }

  disconnectedCallback() {
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    if (this.syncTrackHeightRaf) cancelAnimationFrame(this.syncTrackHeightRaf);
    window.removeEventListener('resize', this.handleResize);
    this.slides.forEach(slide => {
      slide.removeEventListener('click', this.handleSlideClick);
      slide.removeEventListener('keydown', this.handleSlideKey);
    });
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.goPrev);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.goNext);
    this.unbindDragListeners();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  waitForGsap() {
    if (typeof window.gsap !== 'undefined') return Promise.resolve();
    return new Promise(resolve => {
      const check = () => {
        if (typeof window.gsap !== 'undefined') return resolve();
        setTimeout(check, 50);
      };
      check();
    });
  }

  init() {
    this.list = this.querySelector('.wr-timeline__slides');
    this.trim = this.querySelector('.wr-timeline__slider-trim');
    this.sliderWrap = this.querySelector('.wr-timeline__slider-wrap');
    this.activeYearEl = this.querySelector('[data-wr-timeline-active-year]');
    this.slides = Array.from(
      this.querySelectorAll('[data-wr-timeline-slide]')
    );
    this.prevBtn = this.querySelector('[data-wr-timeline-prev]');
    this.nextBtn = this.querySelector('[data-wr-timeline-next]');

    if (!this.list || !this.trim || this.slides.length === 0) return;

    this.totalSlides = this.slides.length;
    this.activeIndex = parseInt(this.dataset.activeIndex || '0', 10) || 0;
    this.loop = this.dataset.loop === 'true';
    this.desktopVisible = Math.max(
      2,
      parseInt(this.dataset.desktopVisible || '6', 10) || 6
    );
    this.mobileSlidesPerView = parseFloat(
      this.dataset.mobileSlidesPerView || '2.25'
    );
    if (
      !Number.isFinite(this.mobileSlidesPerView) ||
      this.mobileSlidesPerView <= 0
    ) {
      this.mobileSlidesPerView = 2.25;
    }

    this.goPrev = () => this.goTo(this.activeIndex - 1, 'prev');
    this.goNext = () => this.goTo(this.activeIndex + 1, 'next');

    if (this.prevBtn) this.prevBtn.addEventListener('click', this.goPrev);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.goNext);

    this.slides.forEach(slide => {
      slide.addEventListener('click', this.handleSlideClick);
      slide.addEventListener('keydown', this.handleSlideKey);
    });

    this.bindDragListeners();

    this.bindTrimResizeObserver();
    this.layout();
    this.updateNavState();
    window.addEventListener('resize', this.handleResize);
    this.scheduleTrackHeightSync();
    this.isInitialized = true;
  }

  bindTrimResizeObserver() {
    if (!this.trim || typeof ResizeObserver === 'undefined') return;

    this.resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry || !this.isInitialized) return;
      const nextWidth = entry.contentRect.width;
      if (nextWidth <= 0) return;
      if (this.trimWidth != null && Math.abs(nextWidth - this.trimWidth) < 1) {
        return;
      }
      this.trimWidth = nextWidth;
      this.desktopTrackHeight = null;
      this.mobileTrackHeight = null;
      this.layout(true);
    });
    this.resizeObserver.observe(this.trim);
    this.trimWidth = this.trim.offsetWidth;
  }

  bindDragListeners() {
    if (!this.sliderWrap) return;

    this.sliderWrap.addEventListener('pointerdown', this.handlePointerDown);
    this.sliderWrap.addEventListener('pointermove', this.handlePointerMove);
    this.sliderWrap.addEventListener('pointerup', this.handlePointerUp);
    this.sliderWrap.addEventListener('pointercancel', this.handlePointerCancel);
  }

  unbindDragListeners() {
    if (!this.sliderWrap) return;

    this.sliderWrap.removeEventListener('pointerdown', this.handlePointerDown);
    this.sliderWrap.removeEventListener('pointermove', this.handlePointerMove);
    this.sliderWrap.removeEventListener('pointerup', this.handlePointerUp);
    this.sliderWrap.removeEventListener(
      'pointercancel',
      this.handlePointerCancel
    );
    this.endDragSession();
  }

  endDragSession() {
    if (this.pointerId == null || !this.sliderWrap) return;

    if (this.sliderWrap.hasPointerCapture(this.pointerId)) {
      this.sliderWrap.releasePointerCapture(this.pointerId);
    }

    this.pointerId = null;
    this.isDragging = false;
    this.dragOffset = 0;
    this.classList.remove('is-dragging');
  }

  canDrag() {
    return this.isMobileLayout() && this.totalSlides > 1 && !this.isAnimating;
  }

  getDragThreshold() {
    const sizes = this.getSizes();
    return Math.max(48, (sizes.iW || 0) * 0.18);
  }

  getClampedDragOffset(dx) {
    if (this.loop) return dx;

    const resistance = 0.35;
    if (this.activeIndex <= 0 && dx > 0) return dx * resistance;
    if (this.activeIndex >= this.totalSlides - 1 && dx < 0) {
      return dx * resistance;
    }

    return dx;
  }

  applyDragOffset(offset) {
    const gsap = window.gsap;
    if (!gsap) return;

    const sizes = this.getSizes();
    this.slides.forEach((slide, i) => {
      const slot = this.slotOf(i, this.activeIndex);
      const dir = slot >= sizes.visibleSlots ? 'prev' : 'next';
      const target = this.targetFor(i, this.activeIndex, sizes, dir);
      gsap.set(slide, { x: target.x + offset });
    });
  }

  snapBackFromDrag() {
    const gsap = window.gsap;
    if (!gsap) return;

    const sizes = this.getSizes();
    let pending = 0;

    this.slides.forEach((slide, i) => {
      const slot = this.slotOf(i, this.activeIndex);
      const dir = slot >= sizes.visibleSlots ? 'prev' : 'next';
      const target = this.targetFor(i, this.activeIndex, sizes, dir);

      pending++;
      gsap.to(slide, {
        ...target,
        duration: 0.35,
        ease: 'power2.out',
        overwrite: true,
        onComplete: () => {
          pending--;
          if (pending === 0) this.isAnimating = false;
        },
      });
    });

    if (pending === 0) this.isAnimating = false;
    else this.isAnimating = true;
  }

  finishDrag() {
    if (!this.isDragging) {
      this.endDragSession();
      return;
    }

    const threshold = this.getDragThreshold();
    const offset = this.dragOffset;
    const shouldGoNext = offset <= -threshold;
    const shouldGoPrev = offset >= threshold;

    this.isDragging = false;
    this.dragOffset = 0;
    this.classList.remove('is-dragging');
    this.endDragSession();

    if (shouldGoNext) {
      this.touchMoved = true;
      this.goNext();
      window.setTimeout(() => {
        this.touchMoved = false;
      }, 0);
      return;
    }

    if (shouldGoPrev) {
      this.touchMoved = true;
      this.goPrev();
      window.setTimeout(() => {
        this.touchMoved = false;
      }, 0);
      return;
    }

    this.snapBackFromDrag();
  }

  handlePointerDown(event) {
    if (!this.canDrag()) return;
    if (event.button !== 0) return;

    this.pointerId = event.pointerId;
    this.pointerStartX = event.clientX;
    this.pointerStartY = event.clientY;
    this.isDragging = false;
    this.dragOffset = 0;
    this.touchMoved = false;

    this.sliderWrap.setPointerCapture(event.pointerId);
  }

  handlePointerMove(event) {
    if (!this.canDrag() || event.pointerId !== this.pointerId) {
      return;
    }

    const dx = event.clientX - this.pointerStartX;
    const dy = event.clientY - this.pointerStartY;

    if (!this.isDragging) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      if (Math.abs(dy) >= Math.abs(dx)) {
        this.endDragSession();
        return;
      }

      this.isDragging = true;
      this.classList.add('is-dragging');
    }

    event.preventDefault();
    this.dragOffset = this.getClampedDragOffset(dx);
    this.applyDragOffset(this.dragOffset);
    this.touchMoved = true;
  }

  handlePointerUp(event) {
    if (event.pointerId !== this.pointerId) return;
    this.finishDrag();
  }

  handlePointerCancel(event) {
    if (event.pointerId !== this.pointerId) return;

    if (this.isDragging) {
      this.isDragging = false;
      this.dragOffset = 0;
      this.classList.remove('is-dragging');
      this.snapBackFromDrag();
    }

    this.endDragSession();
  }

  isMobileLayout() {
    return this.getLayoutMode() === 'mobile' && this.totalSlides > 1;
  }

  scheduleTrackHeightSync() {
    if (this.syncTrackHeightRaf) cancelAnimationFrame(this.syncTrackHeightRaf);
    this.syncTrackHeightRaf = requestAnimationFrame(() => {
      this.syncTrackHeightRaf = requestAnimationFrame(() => this.syncTrackHeight());
    });
  }

  syncTrackHeight() {
    if (!this.list || !this.isInitialized) return;
    const gsap = window.gsap;
    if (!gsap) return;

    const sizes = this.getSizes();
    this.desktopTrackHeight = null;
    this.mobileTrackHeight = null;
    const nextH = this.getTrackHeight(sizes);
    const currentH = parseFloat(this.list.style.height) || this.list.offsetHeight;

    if (Math.abs(nextH - currentH) < 1) return;

    gsap.set(this.list, { height: nextH });
    if (sizes.mode !== 'mobile') {
      this.measureHeightForActive(this.activeIndex, sizes);
    }
  }

  handleResize() {
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      if (this.isDragging) {
        this.classList.remove('is-dragging');
        this.endDragSession();
      }
      this.desktopTrackHeight = null;
      this.mobileTrackHeight = null;
      this.layout(true);
    }, 150);
  }

  handleSlideClick(event) {
    if (this.touchMoved) return;

    const slide = event.currentTarget;
    const idx = parseInt(slide.dataset.index, 10);
    if (Number.isNaN(idx) || idx === this.activeIndex) return;
    const dir = idx > this.activeIndex ? 'next' : 'prev';
    this.goTo(idx, dir);
  }

  handleSlideKey(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleSlideClick(event);
    }
  }

  getLayoutMode() {
    const w = window.innerWidth;
    if (w < 768) return 'mobile';
    if (w < 992) return 'tablet';
    return 'desktop';
  }

  getMobileRowOrder(activeIdx) {
    const order = [];
    for (let i = activeIdx + 1; i < this.totalSlides; i++) order.push(i);
    for (let i = 0; i < activeIdx; i++) order.push(i);
    return order;
  }

  getMobileVisibleSlots() {
    if (this.totalSlides <= 1) return 1;
    const slots = Math.ceil(this.mobileSlidesPerView);
    return Math.min(slots, this.totalSlides - 1);
  }

  getVisibleSlots() {
    const mode = this.getLayoutMode();
    if (mode === 'mobile') return this.getMobileVisibleSlots();
    if (mode === 'tablet') return Math.min(this.totalSlides, 3);
    return Math.min(this.totalSlides, this.desktopVisible);
  }

  getSizes() {
    const mode = this.getLayoutMode();
    const W = this.trim.offsetWidth;
    const visibleSlots = this.getVisibleSlots();
    const gap = mode === 'mobile' ? this.mobileGap : this.gap;

    if (W <= 0) {
      return { W, aW: 0, iW: 0, mode, visibleSlots, gap };
    }

    if (mode === 'mobile') {
      if (this.totalSlides <= 1) {
        return { W, aW: W, iW: W, mode, visibleSlots: 1, gap };
      }
      const cardW =
        (W - gap * (this.mobileSlidesPerView - 1)) / this.mobileSlidesPerView;
      return { W, aW: cardW, iW: cardW, mode, visibleSlots, gap };
    }

    if (visibleSlots <= 1) {
      return { W, aW: W, iW: W, mode, visibleSlots: 1, gap };
    }

    const inactiveCount = visibleSlots - 1;
    const activeRatio =
      mode === 'tablet' ? 0.55 : Math.max(0.28, 0.6 - 0.05 * inactiveCount);

    const aW = W * activeRatio;
    const totalGap = gap * inactiveCount;
    const iW = (W - aW - totalGap) / inactiveCount;

    return { W, aW, iW, mode, visibleSlots, gap };
  }

  slotOf(slideIdx, aIdx) {
    const total = this.totalSlides;
    return (((slideIdx - aIdx) % total) + total) % total;
  }

  mobileTargetFor(slideIdx, activeIdx, sizes, dir) {
    const { W, iW: cardW, visibleSlots, gap } = sizes;

    if (slideIdx === activeIdx) {
      return { x: -W, width: cardW, opacity: 0 };
    }

    const rowOrder = this.getMobileRowOrder(activeIdx);
    const posInRow = rowOrder.indexOf(slideIdx);

    if (posInRow === -1 || posInRow >= visibleSlots) {
      const offRight = W + gap;
      const offLeft = -(cardW + gap);
      return {
        x: dir === 'next' ? offLeft : offRight,
        width: cardW,
        opacity: 0,
      };
    }

    return {
      x: posInRow * (cardW + gap),
      width: cardW,
      opacity: 1,
    };
  }

  targetFor(slideIdx, activeIdx, sizes, dir) {
    if (sizes.mode === 'mobile') {
      return this.mobileTargetFor(slideIdx, activeIdx, sizes, dir);
    }

    const slot = this.slotOf(slideIdx, activeIdx);
    const { W, aW, iW, visibleSlots, gap } = sizes;

    if (slot === 0) {
      return { x: 0, width: aW, opacity: 1 };
    }

    if (slot < visibleSlots) {
      const x = aW + gap + (slot - 1) * (iW + gap);
      return { x, width: iW, opacity: 0.85 };
    }

    const offRight = W + gap;
    const offLeft = -(iW + gap);
    return {
      x: dir === 'next' ? offLeft : offRight,
      width: iW,
      opacity: 0,
    };
  }

  isSlideInteractive(slideIdx, activeIdx, sizes) {
    if (sizes.mode === 'mobile' && this.totalSlides > 1) {
      const posInRow = this.getMobileRowOrder(activeIdx).indexOf(slideIdx);
      return posInRow >= 0 && posInRow < sizes.visibleSlots;
    }
    return this.slotOf(slideIdx, activeIdx) < sizes.visibleSlots;
  }

  updateSlidePointerEvents(sizes, activeIdx = this.activeIndex) {
    this.slides.forEach((slide, i) => {
      slide.style.pointerEvents = this.isSlideInteractive(i, activeIdx, sizes)
        ? 'auto'
        : 'none';
    });
  }

  updateActiveYear(activeIdx = this.activeIndex) {
    if (!this.activeYearEl) return;
    const slide = this.slides[activeIdx];
    const yearEl = slide?.querySelector('.wr-timeline__year');
    if (yearEl) {
      this.activeYearEl.textContent = yearEl.textContent.trim();
    }
  }

  measureHeightForActive(activeIdx, sizes) {
    this.classList.add('is-measuring');
    try {
      if (sizes.mode === 'mobile') {
        return this._measureMobileHeight(activeIdx, sizes);
      }
      return this._measureHeightForActive(activeIdx, sizes);
    } finally {
      this.classList.remove('is-measuring');
    }
  }

  _measureMobileHeight(activeIdx, sizes) {
    const { iW: cardW } = sizes;
    const rowOrder = this.getMobileRowOrder(activeIdx);
    const measureIdx = rowOrder[0];
    if (measureIdx == null) return 0;

    const widthSnap = this.slides.map(s => s.style.width);
    const listHeightSnap = this.list.style.height;
    const detailsSnap = this.slides.map(s => {
      const d = s.querySelector('[data-wr-timeline-details]');
      if (!d) return null;
      return { height: d.style.height, opacity: d.style.opacity };
    });

    this.list.style.height = 'auto';

    this.slides.forEach((slide, i) => {
      slide.style.width = `${cardW}px`;
      const d = slide.querySelector('[data-wr-timeline-details]');
      if (!d) return;
      const showDetails = i !== activeIdx;
      if (showDetails) {
        d.style.height = '';
        d.style.opacity = '1';
      } else {
        d.style.height = '0px';
        d.style.opacity = '0';
      }
    });

    void this.list.offsetHeight;
    const measureSlide = this.slides[measureIdx];
    const h = Math.max(measureSlide?.offsetHeight || 0, 0);

    this.list.style.height = listHeightSnap;
    this.slides.forEach((slide, i) => {
      slide.style.width = widthSnap[i];
      const d = slide.querySelector('[data-wr-timeline-details]');
      if (!d || !detailsSnap[i]) return;
      d.style.height = detailsSnap[i].height;
      d.style.opacity = detailsSnap[i].opacity;
    });

    return h;
  }

  _measureHeightForActive(activeIdx, sizes) {
    const { aW, iW } = sizes;
    const total = this.totalSlides;

    const widthSnap = this.slides.map(s => s.style.width);
    const activeSnap = this.slides.map(s => s.classList.contains('is-active'));
    const listHeightSnap = this.list.style.height;
    const detailsSnap = this.slides.map(s => {
      const d = s.querySelector('[data-wr-timeline-details]');
      if (!d) return null;
      return { height: d.style.height, opacity: d.style.opacity };
    });

    this.list.style.height = 'auto';

    this.slides.forEach((slide, i) => {
      const slot = (((i - activeIdx) % total) + total) % total;
      const isActive = slot === 0;
      slide.style.width = `${isActive ? aW : iW}px`;
      slide.classList.toggle('is-active', isActive);
      const d = slide.querySelector('[data-wr-timeline-details]');
      if (!d) return;
      d.style.height = isActive ? 'auto' : '0px';
      d.style.opacity = isActive ? '1' : '0';
    });

    void this.list.offsetHeight;
    const activeSlide = this.slides[activeIdx];
    const h = Math.max(activeSlide?.offsetHeight || 0, 0);

    const inactiveSlide = this.slides.find((_, i) => i !== activeIdx);
    const inactiveH = inactiveSlide ? inactiveSlide.offsetHeight : 0;

    this.list.style.height = listHeightSnap;
    this.slides.forEach((slide, i) => {
      slide.style.width = widthSnap[i];
      slide.classList.toggle('is-active', activeSnap[i]);
      const d = slide.querySelector('[data-wr-timeline-details]');
      if (!d || !detailsSnap[i]) return;
      d.style.height = detailsSnap[i].height;
      d.style.opacity = detailsSnap[i].opacity;
    });

    const gap = sizes.gap ?? this.gap;
    this.style.setProperty('--wr-tl-details-top', `${inactiveH}px`);
    this.style.setProperty('--wr-tl-details-left', `calc(100% + ${gap}px)`);
    this.style.setProperty('--wr-tl-details-width', `${iW}px`);

    return h;
  }

  measureMaxActiveHeight(sizes) {
    let maxH = 0;
    for (let i = 0; i < this.totalSlides; i++) {
      maxH = Math.max(maxH, this.measureHeightForActive(i, sizes));
    }
    this.desktopTrackHeight = maxH;
    return maxH;
  }

  measureMaxMobileHeight(sizes) {
    let maxH = 0;
    for (let i = 0; i < this.totalSlides; i++) {
      maxH = Math.max(maxH, this._measureMobileHeight(i, sizes));
    }
    this.mobileTrackHeight = maxH;
    return maxH;
  }

  getTrackHeight(sizes) {
    if (sizes.mode === 'desktop') {
      if (this.desktopTrackHeight != null) return this.desktopTrackHeight;
      return this.measureMaxActiveHeight(sizes);
    }
    if (sizes.mode === 'mobile' && this.totalSlides > 1) {
      if (this.mobileTrackHeight != null) return this.mobileTrackHeight;
      return this.measureMaxMobileHeight(sizes);
    }
    return this.measureHeightForActive(this.activeIndex, sizes);
  }

  setDetails(slide, isActive, animate, slideDuration = 0, activeIdx = this.activeIndex) {
    const details = slide.querySelector('[data-wr-timeline-details]');
    if (!details) return;
    const gsap = window.gsap;
    const isMobile = this.isMobileLayout();

    if (isMobile) {
      const slideIdx = parseInt(slide.dataset.index, 10);
      const showDetails = slideIdx !== activeIdx;
      if (showDetails) {
        details.style.height = '';
        details.style.opacity = '1';
        details.style.overflow = 'visible';
      } else {
        details.style.height = '0px';
        details.style.opacity = '0';
        details.style.overflow = 'hidden';
      }
      return;
    }

    if (!animate) {
      gsap.set(details, {
        height: isActive ? 'auto' : 0,
        opacity: isActive ? 1 : 0,
        overflow: 'hidden',
      });
      return;
    }
    if (isActive) {
      gsap.set(details, { height: 0, opacity: 0, overflow: 'hidden' });
      gsap.to(details, {
        height: 'auto',
        opacity: 1,
        duration: 0.4,
        ease: 'power2.out',
        delay: slideDuration,
        overwrite: true,
      });
    } else {
      gsap.to(details, {
        height: 0,
        opacity: 0,
        duration: 0.25,
        ease: 'power2.in',
        overwrite: true,
      });
    }
  }

  fadeOutActiveDetails() {
    if (this.isMobileLayout()) return Promise.resolve();

    const gsap = window.gsap;
    const activeSlide = this.slides[this.activeIndex];
    const details = activeSlide?.querySelector('[data-wr-timeline-details]');
    if (!details || !gsap) return Promise.resolve();

    const opacity = parseFloat(gsap.getProperty(details, 'opacity')) || 0;
    const height = parseFloat(gsap.getProperty(details, 'height')) || 0;
    if (opacity <= 0 && height <= 0) return Promise.resolve();

    return new Promise(resolve => {
      gsap.to(details, {
        height: 0,
        opacity: 0,
        duration: 0.25,
        ease: 'power2.in',
        overwrite: true,
        onComplete: resolve,
      });
    });
  }

  layout(fromResize = false) {
    const gsap = window.gsap;
    if (!gsap) return;
    const sizes = this.getSizes();
    if (sizes.W <= 0) return;
    const isMobile = this.isMobileLayout();

    this.classList.toggle('is-mobile', isMobile);

    const cardH = this.getTrackHeight(sizes);

    Object.assign(this.list.style, {
      position: 'relative',
      display: 'block',
      height: `${cardH}px`,
    });

    this.slides.forEach((slide, i) => {
      Object.assign(slide.style, {
        position: 'absolute',
        top: '0',
        boxSizing: 'border-box',
      });

      const slot = this.slotOf(i, this.activeIndex);
      const dir = slot >= sizes.visibleSlots ? 'prev' : 'next';
      const target = this.targetFor(i, this.activeIndex, sizes, dir);
      gsap.set(slide, target);

      const isActive = isMobile ? i === this.activeIndex : slot === 0;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-current', isActive ? 'true' : 'false');

      const isVisible = isMobile
        ? i !== this.activeIndex &&
          this.getMobileRowOrder(this.activeIndex).indexOf(i) < sizes.visibleSlots
        : slot < sizes.visibleSlots;
      slide.setAttribute('tabindex', isVisible ? '0' : '-1');
      this.setDetails(slide, isActive, false, 0, this.activeIndex);
    });

    this.updateSlidePointerEvents(sizes, this.activeIndex);
    this.updateActiveYear();
    if (fromResize) this.updateNavState();
  }

  goTo(targetIdx, dir) {
    if (this.isAnimating) return;
    if (this.isDragging) this.endDragSession();
    const total = this.totalSlides;

    let newActive;
    if (this.loop) {
      newActive = ((targetIdx % total) + total) % total;
    } else {
      if (targetIdx < 0 || targetIdx > total - 1) return;
      newActive = targetIdx;
    }

    if (newActive === this.activeIndex) return;

    this.isAnimating = true;

    if (this.isMobileLayout()) {
      this.runSlideTransition(newActive, dir);
      return;
    }

    this.fadeOutActiveDetails().then(() =>
      this.runSlideTransition(newActive, dir)
    );
  }

  runSlideTransition(newActive, dir) {
    const gsap = window.gsap;
    const sizes = this.getSizes();
    const gap = sizes.gap ?? this.gap;
    const isMobile = this.isMobileLayout();
    const enterFromX =
      dir === 'next' ? sizes.W + gap : -(sizes.iW + gap);
    const duration = isMobile ? 0.45 : 0.55;

    this.slides.forEach((slide, i) => {
      const oldSlot = this.slotOf(i, this.activeIndex);
      const newSlot = this.slotOf(i, newActive);

      let wasHidden;
      let willBeVisible;
      if (isMobile) {
        const oldOrder = this.getMobileRowOrder(this.activeIndex);
        const newOrder = this.getMobileRowOrder(newActive);
        wasHidden =
          i === this.activeIndex ||
          oldOrder.indexOf(i) >= sizes.visibleSlots ||
          oldOrder.indexOf(i) === -1;
        willBeVisible =
          i !== newActive && newOrder.indexOf(i) < sizes.visibleSlots;
      } else {
        wasHidden = oldSlot >= sizes.visibleSlots;
        willBeVisible = newSlot < sizes.visibleSlots;
      }

      if (wasHidden && willBeVisible) {
        gsap.set(slide, { x: enterFromX, opacity: 0 });
      }
    });

    if (sizes.mode === 'desktop' || isMobile) {
      gsap.set(this.list, { height: this.getTrackHeight(sizes) });
    } else {
      const targetH = this.measureHeightForActive(newActive, sizes);
      gsap.to(this.list, {
        height: targetH,
        duration,
        ease: 'power2.inOut',
        overwrite: true,
      });
    }

    let pending = 0;
    this.slides.forEach((slide, i) => {
      const target = this.targetFor(i, newActive, sizes, dir);
      const isActive = isMobile ? i === newActive : this.slotOf(i, newActive) === 0;

      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-current', isActive ? 'true' : 'false');

      const newOrder = this.getMobileRowOrder(newActive);
      const isVisible = isMobile
        ? i !== newActive && newOrder.indexOf(i) < sizes.visibleSlots
        : this.slotOf(i, newActive) < sizes.visibleSlots;
      slide.setAttribute('tabindex', isVisible ? '0' : '-1');

      if (isMobile) {
        this.setDetails(slide, isActive, false, 0, newActive);
      } else if (isActive) {
        this.setDetails(slide, true, true, duration);
      } else {
        this.setDetails(slide, false, false);
      }

      pending++;
      gsap.to(slide, {
        ...target,
        duration,
        ease: 'power2.inOut',
        overwrite: true,
        onComplete: () => {
          pending--;
          if (pending === 0) {
            this.isAnimating = false;
            this.updateActiveYear(newActive);
          }
        },
      });
    });

    this.activeIndex = newActive;
    this.updateSlidePointerEvents(sizes, newActive);
    this.updateActiveYear(newActive);
    this.updateNavState();
  }

  updateNavState() {
    if (this.loop) return;
    if (this.prevBtn) {
      const disabled = this.activeIndex <= 0;
      this.prevBtn.classList.toggle('is-disabled', disabled);
      this.prevBtn.disabled = disabled;
    }
    if (this.nextBtn) {
      const disabled = this.activeIndex >= this.totalSlides - 1;
      this.nextBtn.classList.toggle('is-disabled', disabled);
      this.nextBtn.disabled = disabled;
    }
  }
}

if (!customElements.get('wr-timeline')) {
  customElements.define('wr-timeline', WrTimeline);
}
